import { useEffect, useState } from 'react'
import { SelectChangeEvent } from '@mui/material/Select'
import stores from '../../stores/store'
import {
  ContentParams,
  ContentParamsHeader,
  ContentParamsGroup,
  ContentParamsItem,
  ContentParamsItemDetail,
  ContentParamsItemTitle,
  ContentParamsItemDescription,
  ContentParamsItemInput,
  ContentParamsItemSelect,
  ContentParamsItemSelectMenuItem,
  ContentParamsItemSelectPaper,
  ContentParamsSelect,
  ContentParamsSelectTitle,
  ContentParamsBody
} from './styles'

type Parameter = {
  nome: string
  tipo: string
  format: string
  descricao: string
  required: string
}

type Filters = {
  nome: string
  tipo: string
  format: string
  descricao: string
  required: string
}

export default function Params() {
  const { jsonSelected: lang } = stores.DataLocaleStore()
  const { content } = stores.DataContentStore()
  const { setUrlCurl } = stores.DataUrlCurlStore()
  const { setTypeCurl } = stores.DataCurlTypeStore()
  const { setProductTypeCurl } = stores.DataCurlProductTypeStore()

  const [tabs, setTabs] = useState<string[]>([])
  const [tabSelected, setTabSelected] = useState('')
  const [url, setUrl] = useState('')
  const [parameters, setParameters] = useState<Parameter[]>([])
  const [filters, setFilters] = useState<Filters[]>([])
  const [inputValues, setInputValues] = useState<{ [key: string]: string }>({})

  useEffect(() => {
    if (content && content.descricao && content.descricao.requisicoes) {
      const requisicoes = content.descricao.requisicoes
      const tabs = Object.keys(requisicoes)
      setTabs(tabs)
      setTabSelected(tabs[0])

      const firstRequisicao = requisicoes[tabs[0]]
      setUrl(firstRequisicao.link)
      setTypeCurl(firstRequisicao.tipo)
      setProductTypeCurl(firstRequisicao.tipo_produto)
      setParameters(firstRequisicao.parameters || [])
      setFilters(firstRequisicao.filters || [])

      setInputValues({})
    }
  }, [content, setTypeCurl, setProductTypeCurl])

  const handleTabChange = (event: SelectChangeEvent<unknown>) => {
    const selectedTab = event.target.value as string
    setTabSelected(selectedTab)

    const selectedRequisicao = content.descricao.requisicoes[selectedTab]
    setUrl(selectedRequisicao.link)
    setTypeCurl(selectedRequisicao.tipo)
    setProductTypeCurl(selectedRequisicao.tipo_produto)
    setParameters(selectedRequisicao.parameters || [])
    setFilters(selectedRequisicao.filters || [])

    setInputValues({})
  }

  const handleInputChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    name: string
  ) => {
    const { value } = event.target
    setInputValues((prevValues) => ({
      ...prevValues,
      [name]: value
    }))
  }

  const replaceUrlParams = (url: string, params: { [key: string]: string }) => {
    return url.replace(/{(.*?)}/g, (_, key) => params[key] || `{${key}}`)
  }

  useEffect(() => {
    const filteredValues = Object.fromEntries(
      Object.entries(inputValues).filter(([_, v]) => v !== '')
    )
    const urlParams = Object.keys(filteredValues).filter((key) =>
      url.includes(`{${key}}`)
    )
    const queryParams = Object.fromEntries(
      Object.entries(filteredValues).filter(([key]) => !urlParams.includes(key))
    )

    const queryString = new URLSearchParams(queryParams).toString()
    const baseUrl = replaceUrlParams(url.split('?')[0], filteredValues)
    const fullUrl = queryString ? `${baseUrl}?${queryString}` : baseUrl
    setUrlCurl(fullUrl)
  }, [url, inputValues, setUrlCurl])

  return (
    <ContentParamsBody>
      <ContentParamsSelect>
        <ContentParamsSelectTitle>{lang.params.title}</ContentParamsSelectTitle>
        <ContentParamsItemSelect
          value={tabSelected}
          onChange={handleTabChange}
          variant="outlined"
          MenuProps={{
            PaperProps: { component: ContentParamsItemSelectPaper }
          }}
        >
          {tabs.map((tab) => (
            <ContentParamsItemSelectMenuItem key={tab} value={tab}>
              {tab}
            </ContentParamsItemSelectMenuItem>
          ))}
        </ContentParamsItemSelect>
      </ContentParamsSelect>

      {parameters.length > 0 && (
        <ContentParams>
          <ContentParamsHeader>
            <span>{lang.params.titleParameters}</span>
          </ContentParamsHeader>
          <ContentParamsGroup>
            {parameters.map((parameter, index) => (
              <ContentParamsItem key={index} onlyOne={parameters.length === 1}>
                <ContentParamsItemDetail>
                  <ContentParamsItemTitle>
                    {parameter.nome}
                    <span>{parameter.tipo}</span>
                    {parameter.required === 'SIM' && (
                      <span className="required">required</span>
                    )}
                  </ContentParamsItemTitle>
                  <ContentParamsItemDescription>
                    {parameter.descricao}
                  </ContentParamsItemDescription>
                </ContentParamsItemDetail>
                <ContentParamsItemInput
                  value={inputValues[parameter.nome] || ''}
                  onChange={(e) => handleInputChange(e, parameter.nome)}
                  variant="outlined"
                  fullWidth
                />
              </ContentParamsItem>
            ))}
          </ContentParamsGroup>
        </ContentParams>
      )}

      {filters.length > 0 && (
        <ContentParams style={{ borderTop: '1px solid', marginTop: '22px' }}>
          <ContentParamsHeader>
            <span>{lang.params.titleFilters}</span>
          </ContentParamsHeader>
          <ContentParamsGroup>
            {filters.map((filter, index) => (
              <ContentParamsItem key={index} onlyOne={filters.length === 1}>
                <ContentParamsItemDetail>
                  <ContentParamsItemTitle>
                    {filter.nome}
                    <span>{filter.tipo}</span>
                    {filter.required === 'SIM' && (
                      <span className="required">required</span>
                    )}
                  </ContentParamsItemTitle>
                  <ContentParamsItemDescription>
                    {filter.descricao}
                  </ContentParamsItemDescription>
                </ContentParamsItemDetail>
                <ContentParamsItemInput
                  value={inputValues[filter.nome] || ''}
                  onChange={(e) => handleInputChange(e, filter.nome)}
                  variant="outlined"
                  fullWidth
                />
              </ContentParamsItem>
            ))}
          </ContentParamsGroup>
        </ContentParams>
      )}

      {parameters.length === 0 && filters.length === 0 && (
        <ContentParams>
          <ContentParamsHeader>
            <span>{lang.params.emptyParameters}</span>
          </ContentParamsHeader>
        </ContentParams>
      )}
    </ContentParamsBody>
  )
}
